import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Paper,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  Slider,
  Snackbar
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { supabase } from './supabaseClient';

// --- Constants --- 
const DEFAULT_DIRECT_SYSTEM_PROMPT = "You are a helpful AI assistant. Answer the user's question directly.";

// Helper function to sort by label
const sortByLabel = (a, b) => (a.label || '').localeCompare(b.label || '');
// --- End Constants --- 

const UserSettingsAdminPage = () => {
  const { userId } = useParams(); // Get target userId from route parameters
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null); // Start as null until fetched
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [userData, setUserData] = useState(null); // State for fetched user details

  // --- ADD getAuthToken HELPER --- 
  const getAuthToken = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.error("getAuthToken: No active session found.");
      // Optionally navigate to login or throw specific error
      throw new Error("Authentication session not found.");
    }
    return session?.access_token || null;
  }, []);
  // --- END HELPER --- 

  // State for models from database
  const [embeddingModels, setEmbeddingModels] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [llmModelsByProvider, setLlmModelsByProvider] = useState({});
  const [providerOptions, setProviderOptions] = useState([]);

  const availableModels = useMemo(() => {
    return selectedProvider ? (llmModelsByProvider[selectedProvider] || []) : [];
  }, [selectedProvider, llmModelsByProvider]);

  // Helper to find provider for a given model
  const findProviderForModel = useCallback((modelValue) => {
    for (const provider in llmModelsByProvider) {
      if (llmModelsByProvider[provider].some(m => m.value === modelValue)) {
        return provider;
      }
    }
    return null;
  }, [llmModelsByProvider]);

  // Fetch embedding and LLM models from database
  useEffect(() => {
    const fetchModels = async () => {
      try {
        // Fetch embedding models
        const { data: embeddingData, error: embeddingError } = await supabase
          .from('embedding_models')
          .select('model_identifier, display_label, provider')
          .eq('is_active', true)
          .order('provider', { ascending: true })
          .order('display_label', { ascending: true });
        
        if (embeddingError) throw embeddingError;
        
        setEmbeddingModels((embeddingData || []).map(row => ({
          value: row.model_identifier,
          label: row.display_label || row.model_identifier
        })));

        // Fetch LLM models and group by provider
        const { data: llmData, error: llmError } = await supabase
          .from('llm_models')
          .select('model_identifier, display_label, provider')
          .eq('is_active', true)
          .order('provider', { ascending: true })
          .order('display_label', { ascending: true });
        
        if (llmError) throw llmError;
        
        // Group LLM models by provider
        const grouped = {};
        (llmData || []).forEach((model) => {
          const provider = model.provider || 'Unknown';
          if (!grouped[provider]) grouped[provider] = [];
          grouped[provider].push({
            value: model.model_identifier,
            label: model.display_label || model.model_identifier
          });
        });
        
        Object.keys(grouped).forEach((p) => grouped[p].sort(sortByLabel));
        const providers = Object.keys(grouped).sort();
        setLlmModelsByProvider(grouped);
        setProviderOptions(providers);
        
      } catch (err) {
        console.error('[UserSettingsAdminPage] Error fetching models:', err);
        setError('Failed to load model options: ' + err.message);
      }
    };
    fetchModels();
  }, []);

  // Fetch settings for the specific user
  useEffect(() => {
    const fetchUserSettings = async () => {
      if (!userId) {
        setError("User ID not found in URL.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        // Fetch user details using the Edge Function
        const token = await getAuthToken(); // Use existing helper
        if (!token) throw new Error("Authentication token not available.");

        const userDetailUrl = `/functions/v1/admin-user-manager/users/${userId}`;
        const userDetailResponse = await fetch(userDetailUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'apikey': (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY || ''
          }
        });
        const userDetailData = await userDetailResponse.json();
        if (!userDetailResponse.ok) {
           throw new Error(userDetailData.error || `Failed to fetch user details: ${userDetailResponse.status}`);
        }
        setUserData(userDetailData.user); // Store fetched user data
        const displayIdentifier = userDetailData.user?.email || userId; // Prefer email, fallback to ID

        // Fetch files for the specific user (using admin JWT implicitly via supabase client)
        const { data, error: fetchError } = await supabase
          .from('user_settings')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle(); // Fetch the specific user's settings

        if (fetchError) throw fetchError;

        let currentSettings = data; // This is the fetched record or null
        
        if (!currentSettings) {
          // If no settings record exists for this user, initialize with defaults
          // (Admins might need the ability to create settings for users who haven't logged in yet)
          console.log(`No existing settings found for user ${userId}, initializing with defaults.`);
          currentSettings = {
            // Provide ALL default values here, ensure keys match DB columns
            embedding_model: 'openai',
            system_prompt: "You are an AI assistant...", // Consider shortening or having a standard default
            llm_model: 'gpt-4o-mini',
            temperature: 0.0, 
            rag_mode: 'similarity_filter',
            enable_reranker: true, 
            direct_system_prompt: '', 
            top_p: 1.0, 
            total_chunks: 50, 
            answer_max_token: 512, 
            top_k: 15, 
            cosine_similarity_threshold: 0.3, 
            enable_grounding_check: false, 
            grounding_similarity_threshold: 0.4, 
          };
        } else {
           // Apply defaults/type checks for fetched data 
           if (typeof currentSettings.enable_reranker !== 'boolean') {
              currentSettings.enable_reranker = true; 
           }
           if (typeof currentSettings.direct_system_prompt !== 'string') {
              currentSettings.direct_system_prompt = ''; 
           }
           if (currentSettings.rag_mode === undefined || currentSettings.rag_mode === null) {
             currentSettings.rag_mode = 'similarity_filter';
           }
           if (typeof currentSettings.enable_grounding_check !== 'boolean') {
             currentSettings.enable_grounding_check = false;
           }
           if (typeof currentSettings.grounding_similarity_threshold !== 'number' || currentSettings.grounding_similarity_threshold < 0 || currentSettings.grounding_similarity_threshold > 1 ) {
             currentSettings.grounding_similarity_threshold = 0.4;
           }
           // Convert temperature to string for UI slider AFTER fetching
           if (currentSettings.temperature !== undefined && currentSettings.temperature !== null) {
             currentSettings.temperature = String(parseFloat(currentSettings.temperature).toFixed(1));
           }
           // Add similar checks for other potentially nullable/missing fields if necessary
        }

        setSettings(currentSettings); // Set the state

        // Set the initial provider based on the fetched/default llm_model
        const initialProvider = findProviderForModel(currentSettings.llm_model);
        setSelectedProvider(initialProvider || (providerOptions.length > 0 ? providerOptions[0] : ''));
        
      } catch (err) {
        console.error("Error fetching user settings:", err);
        setError(`Failed to load settings for user ${userId}: ${err.message}`);
        setSettings({}); // Set empty object on error?
      } finally {
        setLoading(false);
      }
    };

    fetchUserSettings();
  }, [userId]);

  const handleChange = (e, sliderValue) => {
    const { name, value, type, checked } = e.target;
    let newValue;
    if (name === 'grounding_similarity_threshold' || name === 'temperature') {
        newValue = sliderValue !== undefined ? sliderValue : parseFloat(value); 
    } else if (name === 'enable_reranker' || name === 'enable_grounding_check') { // Handle switches
        newValue = checked;
    } else {
        newValue = value;
    }
    setSettings(prev => ({
      ...prev,
      [name]: (name === 'temperature' || name === 'grounding_similarity_threshold') ? Number(newValue) : newValue,
    }));
  };

  const handleProviderChange = (e) => {
    const newProvider = e.target.value;
    setSelectedProvider(newProvider);
    const modelsForNewProvider = llmModelsByProvider[newProvider] || [];
    const firstModelValue = modelsForNewProvider.length > 0 ? modelsForNewProvider[0].value : '';
    setSettings(prev => ({
      ...prev,
      llm_model: firstModelValue
    }));
  };

  const handleModelChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!userId) {
        setError("Cannot save settings: User ID is missing.");
        return;
    }
    setSaving(true);
    setError('');
    setSaveSuccess('');

    const settingsToSave = {
      ...settings,
      temperature: parseFloat(settings.temperature), // Convert back to float
      // Ensure boolean values are correctly typed
      enable_reranker: !!settings.enable_reranker,
      enable_grounding_check: !!settings.enable_grounding_check,
      // Ensure numeric values are numbers
      grounding_similarity_threshold: parseFloat(settings.grounding_similarity_threshold),
      // Remove potentially added state fields not in DB table schema
      // (assuming 'id' might be fetched but shouldn't be sent on update)
      id: undefined, 
    };
    
    // Remove undefined keys before sending to Supabase
    Object.keys(settingsToSave).forEach(key => {
      if (settingsToSave[key] === undefined) {
        delete settingsToSave[key];
      }
    });

    console.log("Attempting to save settings for user:", userId, settingsToSave);

    try {
        // Admin RLS should allow this update based on the target user_id
        const { error: updateError } = await supabase
          .from('user_settings')
          .update(settingsToSave) // Use update, not upsert, as we fetched/created defaults
          .eq('user_id', userId);
          // Note: If the user had NO record before, this update will do nothing.
          // We should use upsert if admins can create settings for users.
          // Let's switch to upsert for robustness.
        /*
        const { error: upsertError } = await supabase
          .from('user_settings')
          .upsert({ ...settingsToSave, user_id: userId }, { onConflict: 'user_id' }); 
        */
       // Re-evaluating: Let's stick with upsert as it handles both create & update
       const { error: upsertError } = await supabase
         .from('user_settings')
         .upsert({ ...settingsToSave, user_id: userId }, { onConflict: 'user_id' });


        if (upsertError) throw upsertError;

        setSaveSuccess('Settings saved successfully!');
        setOpenSnackbar(true);
        // Maybe fetch again after save to ensure UI reflects DB state?
        // fetchUserSettings(); 
    } catch (err) {
        console.error(`Error saving settings for user ${userId}:`, err);
        setError(`Error saving settings: ${err.message}`);
    } finally {
        setSaving(false);
        setTimeout(() => setSaveSuccess(''), 5000);
    }
  };

  if (loading) {
    return <Container><Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box></Container>;
  }
  if (!settings) {
      return <Container><Alert severity="warning" sx={{ mt: 2 }}>No settings data loaded or user has no settings yet.</Alert></Container>;
  }

  // --- RENDER THE FORM --- 
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link component={RouterLink} underline="hover" color="inherit" to="/admin">
          Admin Panel
        </Link>
        <Link component={RouterLink} underline="hover" color="inherit" to={`/admin/user/${userId}`}>
          User Details ({userData?.email || userData?.user_metadata?.full_name || userId})
        </Link>
        <Typography color="text.primary">Settings</Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom>
        Settings for User: {userData?.email || userData?.user_metadata?.full_name || userId}
      </Typography>
      
      {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}
      {saveSuccess && (
         <Snackbar
            open={openSnackbar}
            autoHideDuration={5000}
            onClose={() => setOpenSnackbar(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={() => setOpenSnackbar(false)} severity="success">
              {saveSuccess}
            </Alert>
          </Snackbar>
        )}

      <Paper sx={{ p: 3, mt: 2 }}>
         {/* Embedding Model */} 
         <TextField select fullWidth margin="normal" label="Embedding Model" name="embedding_model" value={settings.embedding_model || ''} onChange={handleChange} disabled={loading || saving}>
            {embeddingModels.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
         </TextField>
         
         {/* System Prompt */} 
         <TextField fullWidth margin="normal" label="System Prompt (RAG Mode)" name="system_prompt" value={settings.system_prompt || ''} onChange={handleChange} multiline rows={6} disabled={loading || saving}/>
         
         {/* LLM Provider */} 
         <FormControl fullWidth margin="normal" disabled={loading || saving}>
            <InputLabel id="provider-select-label">LLM Provider</InputLabel>
            <Select labelId="provider-select-label" value={selectedProvider} label="LLM Provider" onChange={handleProviderChange}>
                {providerOptions.map((provider) => (<MenuItem key={provider} value={provider}>{provider}</MenuItem>))}
            </Select>
         </FormControl>
         
         {/* LLM Model */} 
         <FormControl fullWidth margin="normal" disabled={loading || saving || !selectedProvider}>
            <InputLabel id="model-select-label">LLM Model</InputLabel>
            <Select labelId="model-select-label" name="llm_model" value={settings.llm_model || ''} label="LLM Model" onChange={handleModelChange}>
                {availableModels.length === 0 && selectedProvider && (<MenuItem disabled value="">No models available for {selectedProvider}</MenuItem>)}
                {availableModels.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
            </Select>
         </FormControl>
         
         {/* Temperature */} 
         <Box sx={{ pl: 1, pr: 1, mt: 2 }}>
            <Typography gutterBottom variant="body2">Creativity (Temperature): {parseFloat(settings.temperature || 0).toFixed(1)}</Typography>
            <Slider name="temperature" aria-label="Temperature" value={parseFloat(settings.temperature || 0)} onChange={handleChange} step={0.1} marks min={0.0} max={2.0} valueLabelDisplay="auto" disabled={loading || saving} />
         </Box>
         
         {/* RAG Strategy */} 
         <TextField select fullWidth margin="normal" label="RAG Strategy" name="rag_mode" value={settings.rag_mode || 'similarity_filter'} onChange={handleChange} disabled={loading || saving} helperText="Similarity Filter is faster, Reranker may be more relevant.">
            <MenuItem key="similarity_filter" value="similarity_filter">Similarity Filter (Default)</MenuItem>
            <MenuItem key="reranker" value="reranker">Reranker (Slower, Potentially Better)</MenuItem>
            <MenuItem key="two_stage_summary" value="two_stage_summary">Two-Stage (Summaries First)</MenuItem>
         </TextField>
         
         {/* Enable Reranker */} 
         <FormControlLabel control={<Switch checked={settings.enable_reranker} onChange={handleChange} name="enable_reranker" disabled={loading || saving} />} label="Enable Reranker (Improves Relevance)" sx={{ mt: 2, display: 'block' }} />
         <Typography variant="caption" display="block" gutterBottom sx={{ pl: 1 }}>Disabling may speed up responses but potentially reduce answer quality.</Typography>
         
         {/* Direct System Prompt */} 
         <TextField fullWidth margin="normal" label="Direct Mode System Prompt" name="direct_system_prompt" value={(settings.direct_system_prompt && settings.direct_system_prompt.trim()) ? settings.direct_system_prompt : DEFAULT_DIRECT_SYSTEM_PROMPT} onChange={handleChange} multiline rows={4} disabled={loading || saving} helperText="System prompt used when 'Direct Mode' is enabled in chat. Leave blank for default." sx={{mt: 1}} />
         
         {/* Enable Grounding Check */} 
         <FormControlLabel control={<Switch checked={settings.enable_grounding_check} onChange={handleChange} name="enable_grounding_check" disabled={loading || saving} />} label="Enable Answer Grounding Check" sx={{ mt: 1, display: 'block' }} />
         <Typography variant="caption" display="block" gutterBottom sx={{ pl: 1 }}>Checks if the answer likely came from the context (adds slight delay).</Typography>
         
         {/* Grounding Threshold Slider (Conditional) */} 
         {settings.enable_grounding_check && (
            <Box sx={{ pl: 1, pr: 1, mt: 1 }}>
                <Typography gutterBottom variant="caption">Grounding Threshold: {settings.grounding_similarity_threshold.toFixed(2)}</Typography>
                <Slider name="grounding_similarity_threshold" aria-label="Grounding Similarity Threshold" value={settings.grounding_similarity_threshold || 0.4} onChange={handleChange} step={0.05} marks min={0.0} max={1.0} valueLabelDisplay="auto" disabled={loading || saving} />
            </Box>
         )}
         
         {/* Save Button */} 
         <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={loading || saving}>
              {saving ? <CircularProgress size={24} /> : 'Save Settings'}
            </Button>
         </Box>
      </Paper>
    </Container>
  );
};

export default UserSettingsAdminPage; 